import { CSRF_HEADER, type ErrorResponse } from '@flux/shared';
import { getCsrfToken } from './csrf.js';
import { notifyUnauthorized } from './unauthorized.js';

/**
 * Erreur HTTP typée renvoyée par `apiFetch` — miroir de `ErrorResponse`
 * (`{error:{message, detail?}}`, `04` §6). `message` est toujours en français
 * courant et affichable tel quel (toast, formulaire) ; `detail` est le
 * technique repliable (`02` §0), absent en production.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | undefined;

  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

export type QueryValue = string | number | boolean | undefined;
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function appendQuery(url: URL, params: QueryParams | undefined): void {
  if (!params) {
    return;
  }
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const entry of value) {
        if (entry !== undefined) {
          url.searchParams.append(key, String(entry));
        }
      }
    } else {
      url.searchParams.set(key, String(value));
    }
  }
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
}

async function parseErrorBody(response: Response): Promise<{ message: string; detail?: string }> {
  try {
    const data = (await response.json()) as Partial<ErrorResponse>;
    if (data.error?.message) {
      return data.error.detail === undefined
        ? { message: data.error.message }
        : { message: data.error.message, detail: data.error.detail };
    }
  } catch {
    // Réponse non-JSON (ex. erreur réseau côté proxy) : on retombe sur un message générique.
  }
  return { message: 'Une erreur technique est survenue. Réessayez.' };
}

/**
 * Client HTTP unique de l'interface — `apps/web/README-shell.md` documente les
 * conventions. Envoie toujours les cookies (`credentials: 'include'`), pose
 * l'en-tête CSRF sur toute mutation, et lève une `ApiError` sur tout statut
 * non 2xx.
 *
 * Sur 401 (session expirée/absente) et sur un 403 déclenché par une mutation
 * (donc, dans cette application à rôle unique, systématiquement un rejet CSRF
 * — `04` §6, il n'existe pas d'autre 403 métier), prévient le reste de
 * l'application via `notifyUnauthorized` : `main.tsx` y répond en vidant le
 * jeton CSRF et en renvoyant vers `/connexion`.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const url = new URL(path, window.location.origin);
  appendQuery(url, options.query);

  const headers: Record<string, string> = {};
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  if (MUTATING_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token) {
      headers[CSRF_HEADER] = token;
    }
  }

  const response = await fetch(url.pathname + url.search, {
    method,
    credentials: 'include',
    headers,
    ...(body !== undefined ? { body } : {}),
    ...(options.signal !== undefined ? { signal: options.signal } : {}),
  });

  if (response.status === 401 || (response.status === 403 && MUTATING_METHODS.has(method))) {
    notifyUnauthorized();
    const { message, detail } = await parseErrorBody(response);
    throw new ApiError(response.status, message, detail);
  }

  if (!response.ok) {
    const { message, detail } = await parseErrorBody(response);
    throw new ApiError(response.status, message, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Déclenche le téléchargement d'un export (CSV) sans quitter la page : les
 * exports sont de simples `GET` authentifiés par cookie (pas de CSRF requis,
 * `04` §6), donc une ancre suffit — pas besoin de `fetch` + `Blob`.
 */
export function downloadFromApi(path: string, query?: QueryParams): void {
  const url = new URL(path, window.location.origin);
  appendQuery(url, query);
  const link = document.createElement('a');
  link.href = url.pathname + url.search;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
